import type {
  ComparisonCandidate,
  FloorplanComparison
} from "./requestFloorplanComparison";

export const pricingCheckedAt = "2026-09-15";
// Standard paid API list prices, USD per million tokens. No cache discounts,
// account credits, taxes or negotiated rates. Exact model matches only.
export function importTokenRates(
  provider: string,
  model: string,
  startedAt: number
) {
  if (provider === "claude" && model === "claude-opus-5-5")
    return {
      input: 4,
      output: 20,
      source: "https://platform.claude.com/docs/en/models/opus-5-5/overview",
      note: "Standard uncached rates. Claude pricing checked 2026-09-29."
    };
  if (provider === "openai" && model === "gpt-6.1-sol")
    return {
      input: 2,
      output: 10,
      source: "https://developers.openai.com/api/docs/models/gpt-6.1-sol",
      note: "Standard uncached rates; long-context surcharge above 272k input tokens. Sol pricing checked 2026-09-30."
    };
  if (provider === "openai" && model === "gpt-6-astra")
    return {
      input: 10,
      output: 50,
      source: "https://developers.openai.com/api/docs/models/gpt-6-astra",
      note: "Standard uncached rates; long-context surcharge above 272k input tokens."
    };
  if (provider === "xai" && model === "grok-4.6")
    return {
      input: 2,
      output: 6,
      source: "https://docs.x.ai/developers/grok-4-6",
      note: "Standard uncached rates."
    };
  if (provider === "gemini" && model === "gemini-3.8-flash") {
    const introductory = startedAt < Date.parse("2027-01-01T00:00:00Z");
    return {
      input: introductory ? 0.75 : 1.5,
      output: introductory ? 3.75 : 7.5,
      source: "https://ai.google.dev/gemini-api/docs/latest-model#whats-new",
      note: introductory
        ? "Introductory pricing through December 31, 2026."
        : "Standard rates from January 1, 2027."
    };
  }
  return undefined;
}
export function estimateImportCost(
  candidate: ComparisonCandidate,
  startedAt: number
) {
  const rates = importTokenRates(
    candidate.provider,
    candidate.model,
    startedAt
  );
  const areas = candidate.areas.map((area) => {
    const input = area.usage?.input_tokens,
      output = area.usage?.output_tokens;
    if (
      !rates ||
      input === undefined ||
      output === undefined ||
      !Number.isFinite(input) ||
      !Number.isFinite(output) ||
      input < 0 ||
      output < 0
    )
      return { id: area.id, usd: undefined };
    const longContext = candidate.provider === "openai" && input > 272_000;
    // output_tokens already includes reasoning; never add it a second time.
    return {
      id: area.id,
      usd:
        (input * rates.input * (longContext ? 2 : 1) +
          output * rates.output * (longContext ? 1.5 : 1)) /
        1_000_000
    };
  });
  const reported = areas.filter((a) => a.usd !== undefined);
  return {
    currency: "USD",
    checkedAt: pricingCheckedAt,
    rates,
    basis:
      "Standard paid API list prices, without cache discounts, cache-write fees, taxes or account credits.",
    usd: reported.length
      ? reported.reduce((sum, a) => sum + a.usd!, 0)
      : undefined,
    partial:
      candidate.status === "in_progress" ||
      !areas.length ||
      reported.length !== areas.length,
    areas
  };
}
export function importCostReport(snapshot: FloorplanComparison) {
  const candidates = snapshot.candidates.map((c) => ({
    ...c,
    estimatedCost: estimateImportCost(c, snapshot.startedAt)
  }));
  const reported = candidates.filter((c) => c.estimatedCost.usd !== undefined);
  return {
    ...snapshot,
    candidates,
    estimatedCost: {
      currency: "USD",
      checkedAt: pricingCheckedAt,
      usd: reported.length
        ? reported.reduce((sum, c) => sum + c.estimatedCost.usd!, 0)
        : undefined,
      partial: candidates.some(
        (c) => c.estimatedCost.partial || c.estimatedCost.usd === undefined
      )
    }
  };
}
export const formatImportCost = (usd: number | undefined) =>
  usd === undefined ? "Unavailable" : `$${usd.toFixed(4)}`;
